import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Database, Smartphone, Globe, Server, Code, Cloud } from 'lucide-react';
import { services } from '../../data/mock';

const iconMap = {
  database: Database,
  smartphone: Smartphone,
  globe: Globe,
  server: Server,
  code: Code,
  cloud: Cloud,
};

const ServicesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 reveal">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-1.5 bg-[#ff6b35]/10 text-[#ff6b35] text-sm font-semibold rounded-full mb-4">
              Our Services
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              What We Do Best
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold hover:gap-3 transition-all"
          >
            View All Services
            <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isLarge = index === 0;
            
            return (
              <div
                key={service.id}
                className={`group bg-white rounded-3xl p-8 border border-gray-100 hover:border-[#ff6b35]/20 hover:shadow-xl hover:shadow-[#ff6b35]/5 transition-all duration-500 hover:-translate-y-2 reveal-scale ${
                  isLarge ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
                style={{ transitionDelay: `${index * 0.05}s` }}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gray-100 group-hover:bg-[#ff6b35] flex items-center justify-center mb-6 transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[#ff6b35]/20">
                  <IconComponent className="w-7 h-7 text-gray-600 group-hover:text-white transition-colors duration-300" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>

                <ul className="space-y-2 mb-6">
                  {service.items.slice(0, 3).map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ff6b35]"></span>
                      {item}
                    </li>
                  ))}
                </ul>

                {/* Link */}
                <Link
                  to={service.href}
                  className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold text-sm group-hover:gap-3 transition-all"
                >
                  Learn More
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
